import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";

// Quién es el dueño del título de la pantalla.
//
// El header de la app (newAdminMenu) rotula la ruta con el label del ítem de
// menú, y muchas vistas vuelven a rotularse a sí mismas con un <h1> idéntico:
// "Torre de control" aparecía dos veces con 40px de diferencia (tres, contando
// el ítem activo del sidebar).
//
// En vez de borrar títulos a mano vista por vista — que rompería los casos
// donde el header aporta el PADRE ("Mesa de ayuda") y la página el HIJO
// ("Contactos"), que sí es información —, la página DECLARA su título y el
// header calla solo cuando dicen exactamente lo mismo.
//
// La declaración se hace en useLayoutEffect: corre antes del paint, así que el
// header nunca alcanza a pintar el título duplicado (cero parpadeo).

const PageTitleContext = createContext({ pageTitle: "", setPageTitle: () => {} });

export function PageTitleProvider({ children }) {
  const [pageTitle, setPageTitle] = useState("");
  const value = useMemo(() => ({ pageTitle, setPageTitle }), [pageTitle]);
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

/** Lo consume el header para decidir si su propio título sobra. */
export function usePageTitleOwner() {
  return useContext(PageTitleContext).pageTitle;
}

/**
 * Declara que ESTA vista ya muestra `title` como encabezado propio.
 *
 * Se limpia al desmontar para que la siguiente ruta no herede el título de la
 * anterior (si no, un header quedaría mudo en una pantalla que sí lo necesita).
 * Acepta cualquier cosa: solo se registra cuando es texto, porque comparar
 * nodos de React no tendría sentido.
 * @param {unknown} title
 */
export function useDeclarePageTitle(title) {
  const { setPageTitle } = useContext(PageTitleContext);
  const declared = typeof title === "string" ? title : "";

  const clear = useCallback(() => setPageTitle(""), [setPageTitle]);

  useLayoutEffect(() => {
    setPageTitle(declared);
    return clear;
  }, [declared, setPageTitle, clear]);
}

/** Compara títulos ignorando mayúsculas, tildes y espacios sobrantes. */
export function sameTitle(a, b) {
  const norm = (s) =>
    (s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  const na = norm(a);
  return !!na && na === norm(b);
}

export default PageTitleContext;
